import React, { useState, useEffect } from 'react';
import { Tooltip, Spin, Alert } from 'antd';
import { Ticket, AF_TITLE, AF_PROJECT, AF_STATE, AF_ASSIGNED_TO, AF_GOV_PROJECT, AF_CAPEX, AF_PRIORITY_DRIVER, AF_TAGS } from '../../services/Ticket';
import AzureDevOpsService from '../../services/AzureDevOpsService';
import { AZURE_DEVOPS_BASE_TICKET_URI } from '../../Common/Constants';

interface ParentChainProps {
  selectedTicket: Ticket;
  azureDevOpsService: AzureDevOpsService;
  onSelectTicket?: (ticket: Ticket) => void; // Callback to open a parent ticket in ChartMenu
}

interface ParentInfo {
  id: number;
  title: string;
  project: string;
  state: string;
  assignee: string;
  govProject: string;
  capex: string;
  priorityDriver: string;
  url: string;
  tags: string;
}

const ParentChain: React.FC<ParentChainProps> = ({ selectedTicket, azureDevOpsService, onSelectTicket }) => {
  const [parentChain, setParentChain] = useState<ParentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchParentData = async () => {
      setLoading(true);
      setError(null);

      try {
        const workItemCache = new Map<string, any>();
        
        const currentWorkItem = await azureDevOpsService.fetchWorkItemById(selectedTicket.id);
        if (!currentWorkItem) {
          throw new Error('Failed to fetch current work item');
        }

        const parents = await azureDevOpsService.fetchParentChain(currentWorkItem, workItemCache);
        
        const parentInfos: ParentInfo[] = parents.map(parent => ({
          id: parent.id,
          title: parent.fields?.[AF_TITLE] || '',
          project: parent.fields?.[AF_PROJECT] || '',
          state: parent.fields?.[AF_STATE] || '',
          assignee: parent.fields?.[AF_ASSIGNED_TO]?.displayName || 'Unassigned',
          govProject: parent.fields?.[AF_GOV_PROJECT] || '',
          capex: parent.fields?.[AF_CAPEX] || '',
          priorityDriver: parent.fields?.[AF_PRIORITY_DRIVER] || '',
          url: parent.url || '',
          tags: parent.fields?.[AF_TAGS] || ''
        }));

        setParentChain(parentInfos.reverse());

      } catch (err) {
        console.error('Error fetching parent data:', err);
        setError('Failed to load parent information');
      } finally {
        setLoading(false);
      }
    };

    fetchParentData();
  }, [selectedTicket.id, azureDevOpsService]);

  const createParentTooltip = (parent: ParentInfo): string => {
    return `<div style='white-space: nowrap; margin: 1em;'>
      <strong>[${parent.id}] ${parent.title}</strong>
      <br/>Project: ${parent.project}
      <br/>Status: ${parent.state}
      <br/>Assignee: ${parent.assignee}
      <br/>GovProject: ${parent.govProject || 'Not set'}
      <br/>CapEx: ${parent.capex || 'Not set'}
      <br/>Priority Driver: ${parent.priorityDriver || 'Not set'}
      <br/>Tags: ${parent.tags || 'Not set'}
    </div>`;
  };

  if (parentChain.length === 0 && !loading && !error) {
    return null; // Don't show anything if no parents
  }

  if (loading) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <Spin size="small" /> Loading parent information...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <Alert message={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
        <strong>Parent Hierarchy:</strong>
      </div>
      <div>
        {parentChain.map((parent, index) => (
          <div key={parent.id} style={{ marginBottom: '4px' }}>
            <Tooltip title={<div dangerouslySetInnerHTML={{ __html: createParentTooltip(parent) }} />}>
              <span style={{ fontSize: '14px' }}>
                {'  '.repeat(index)}{index === 0 ? '' : '↳ '}
                <a
                  href={`${AZURE_DEVOPS_BASE_TICKET_URI}${parent.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  [{parent.id}]
                </a>
                {' '}
                {onSelectTicket ? (
                  <span
                    onClick={async () => {
                      const fullTicket = await azureDevOpsService.fetchWorkItemById(parent.id);
                      if (fullTicket) {
                        const ticket = new Ticket(fullTicket);
                        ticket.children = [];

                        // Fetch children if they exist
                        if (ticket.childrenIds && ticket.childrenIds.length > 0) {
                          const childrenDetails = await Promise.all(
                            ticket.childrenIds.map(async (childId: number) => {
                              try {
                                const resultChild = await azureDevOpsService.fetchWorkItemById(childId);
                                const childTicket = new Ticket(resultChild, ticket.initiative);
                                return childTicket;
                              } catch (err) {
                                console.error("Error fetching child ticket:", err);
                                return null;
                              }
                            })
                          );
                          ticket.children = childrenDetails.filter((child): child is Ticket => child !== null);
                        }

                        onSelectTicket(ticket);
                      }
                    }}
                    style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                  >
                    {parent.title}
                  </span>
                ) : (
                  <span>{parent.title}</span>
                )}
                {' '}[{parent.project}]
              </span>
            </Tooltip>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ParentChain;
